import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { RootState } from "../../services/redux/store";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import eligibilityCheckServices from "../../services/api/service/eligibilityCheckService";
import Breadcrumbs from "../../components/ui/Breadcrumbs";
import CustomDivider from "../../components/common/CustomDivider";

// --- Interfaces ---
interface MandatoryDocument {
  id: string;
  docName: string;
  description: string;
}
interface MandatoryDocumentsViewProps {
  // No props for now, using internal dummy data.
  // Can be extended to accept a list of documents as props for reusability.
}

const MandatoryDocumentsView: React.FC<MandatoryDocumentsViewProps> = () => {
  const location = useLocation();
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("md"));
  const tabIndex: number = location?.state?.tabIndex || 0;
  const formUserData: any = useSelector(
    (state: RootState) => state.stepper.formData
  );
  const applicationNumber = useSelector(
    (state: RootState) => state.stepper.trackingId
  );
  const [isLoading, setIsLoading] = useState(false);
  const [mandatoryDocumentData, setMandatoryDocumentData] = useState<
    MandatoryDocument[]
  >([]);

  const breadcrumbItems = isXsOrSm
    ? [
        {
          label: `${formUserData.courseName || ""} [#${
            applicationNumber || ""
          }]`,
          path: "",
        },
        { label: "Mandatory Documents" },
      ]
    : [
        { label: "Dashboard", path: "/application" },
        {
          label: `${formUserData.courseName || ""} [#${
            applicationNumber || ""
          }]`,
          path: "",
        },
        { label: "Mandatory Documents" },
      ];

  const getViewMandatoryDocuments = async () => {
    try {
      setIsLoading(true);
      const payload = {
        trackingId: applicationNumber,
      };
      const response = await eligibilityCheckServices.getViewMandatoryDocuments(
        payload
      );
      setMandatoryDocumentData(response?.data?.data);
    } catch (error: any) {
      console.log(error, "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getViewMandatoryDocuments();
  }, [tabIndex, formUserData]);

  // Skeleton loader for list items
  const renderSkeleton = () => (
    <List sx={{ p: 0 }}>
      {[...Array(mandatoryDocumentData.length || 4)].map((_, idx) => (
        <ListItem
          key={idx}
          sx={{
            mb: 1,
            bgcolor: "#ffffff",
            border: "1px solid #eee",
            borderRadius: "8px",
            py: 1.5,
            px: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Skeleton variant="text" width={120} height={24} />
          <Skeleton variant="text" width={200} height={18} />
        </ListItem>
      ))}
    </List>
  );

  return (
    <>
      <Box sx={{ width: "100%", padding: "0px 0px 18px 0px" }}>
        <Breadcrumbs items={breadcrumbItems} />
      </Box>

      <div className="new-application">
        <div className="card-contaner">
          <Typography
            className="section-heading"
            sx={{ color: "#5d586c !important" }}
          >
            Mandatory Documents
          </Typography>

          {isXsOrSm && <CustomDivider />}

          <Box sx={{ mt: "25px !important" }}>
            {isLoading ? (
              renderSkeleton()
            ) : (
              <List sx={{ p: 0 }}>
                {mandatoryDocumentData?.length == 0 ? (
                  <>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        fontSize: "15px",
                        color: "#6c6c6cff",
                        height: "300px",
                        textAlign: "center",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      No documents uploaded!
                    </Typography>
                  </>
                ) : (
                  mandatoryDocumentData.map((doc) => (
                    <ListItem
                      key={doc.id}
                      sx={{
                        mb: 1, // Margin bottom between list items
                        bgcolor: "#ffffff", // White background for each item
                        border: "1px solid #eee", // Light grey border
                        borderRadius: "8px", // Rounded corners
                        py: 1.5, // Vertical padding
                        px: 2, // Horizontal padding
                        display: "flex",
                        flexDirection: "column", // Stack primary and secondary text
                        alignItems: "flex-start", // Align text to the left
                      }}
                    >
                      <ListItemText
                        primary={doc.docName}
                        secondary={doc.description}
                        primaryTypographyProps={{
                          fontWeight: 600,
                          fontSize: "15px",
                          color: "#5d586c",
                        }}
                        secondaryTypographyProps={{
                          fontSize: "0.8rem",
                          color: "#757575",
                        }}
                        sx={{ my: 0 }} // Remove default ListItemText vertical margin
                      />
                    </ListItem>
                  ))
                )}
              </List>
            )}
          </Box>
        </div>
      </div>
    </>
  );
};

export default MandatoryDocumentsView;
